// libraries
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Avatar, Box, Flex, Image, Text, Heading, useToast, VStack, IconButton, Spinner, Center, useColorModeValue } from '@chakra-ui/react';
import { MdCheck, MdClose } from 'react-icons/md';

// components
import { Panel, InfoRow, InfoRowEditable } from '@bemap/ui';
import { useAuth, ITeam, formatDate, base64 } from '@bemap/utils';
import { useTranslation } from 'react-i18next';

// services
import { getTeamInfo, uploadTeamProfilePhoto } from '../../services/team-settings-service';

function TeamSettings() {
  const { user } = useAuth()
  const { t } = useTranslation()
  const toast = useToast()
  const textColor = useColorModeValue('gray.800', 'white');
  // consts
  const initialTeamInfo: Partial<ITeam> = {
    name: '',
    userId: 0,
    personalTeam: false,
    type: '',
    profilePhotoPath: '',
  };

  // state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [teamInfo, setTeamInfo] = useState<Partial<ITeam>>(initialTeamInfo);
  const [loading, setLoading] = useState<boolean>(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [update, setUpdate] = useState<number | null>(null);

  useEffect(() => {
    if (user?.id && user?.currentTeamId) {
      loadInfo();
    }
  }, [user, update]);

  const loadInfo = async () => {
    if (!user?.currentTeamId) return;

    setLoading(true)
    toast.closeAll()
    toast({
      id: 'carregando',
      title: t('general.loading'),
      status: 'loading',
      isClosable: true,
    })
    try {
      const response = await getTeamInfo(user.currentTeamId)
      toast.closeAll()
      setLoading(false)
      setTeamInfo(response.message)
    } catch (error) {
      toast({
        title: t('general.error'),
        status: 'error',
        isClosable: true,
      })
    }
  }

  function handleChange(event: any) {
    setTeamInfo({
      ...teamInfo,
      [event.target.name]: event.target.value,
    });
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const fileReader = new FileReader();
      fileReader.onload = () => {
        setPreviewUrl(fileReader.result as string);
      };
      fileReader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !user?.currentTeamId) {
      toast({
        title: t('general.empty_file'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const formData = new FormData();
    formData.append('image', selectedFile);

    setLoading(true)
    try {
      await uploadTeamProfilePhoto(user.currentTeamId, formData);
      setLoading(false)
      setUpdate(Date.now())
      handleCancel()
      toast({
        title: t('general.sucess'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      setLoading(false)
      toast({
        title: t('general.error'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  // main
  return (
    <Flex direction={{ base: 'column', lg: 'row' }} gap={4} w={'100%'}>
      <VStack gap={4} w={'100%'} alignItems='flex-start'>
        <Text mt='20px' color={textColor} fontSize='22px' fontWeight='700' lineHeight='100%'>
          {t('account.team_settings_title')}
        </Text>
        <Panel padding={4}>
          <Flex direction="column" gap={4} alignItems='flex-start'>
            {teamInfo.type != "admin" && (
              <VStack spacing={4} align="center">
                <Box position="relative">
                  <Image
                    src={previewUrl || teamInfo?.profilePhotoPath}
                    fallbackSrc='https://via.placeholder.com/350x90'
                    borderRadius="md"
                    maxW="350px"
                    w="100%"
                  />
                  <Box
                    onClick={() => fileInputRef.current?.click()}
                    cursor={'pointer'}
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    opacity="0"
                    bg="blackAlpha.600"
                    color="white"
                    display="flex"
                    padding={'20px'}
                    textAlign={'center'}
                    alignItems="center"
                    justifyContent="center"
                    transition="opacity 0.2s"
                    _hover={{ opacity: 1 }}
                    borderRadius="md"
                  >
                    <Text fontSize="sm">{t('account.team_settings_logo_cta')}</Text>
                  </Box>
                </Box>
                <input
                  type="file"
                  hidden
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/*"
                />
                {selectedFile && (
                  <Flex gap={6}>
                    <IconButton
                      borderRadius={'50%'}
                      aria-label='Cancelar'
                      colorScheme="red"
                      size="sm"
                      onClick={handleCancel}
                    >
                      <MdClose />
                    </IconButton>
                    <IconButton
                      borderRadius={'50%'}
                      aria-label='Salvar'
                      colorScheme="green"
                      size="sm"
                      onClick={handleUpload}
                    >
                      <MdCheck />
                    </IconButton>
                  </Flex>
                )}
                <Text fontSize="sm">{t('account.team_settings_logo_description')}</Text>
              </VStack>
            )}

            <Flex direction="row" gap={4} w={'100%'}>
              <InfoRowEditable
                title={t('account.team_settings_name')}
                value={teamInfo.name || ''}
                onChange={handleChange}
                isLoading={loading}
              />
              <InfoRow
                title={t('account.team_settings_created')}
                text={formatDate(teamInfo.createdAt || '')}
                isLoading={loading}
              />
            </Flex>
          </Flex>
        </Panel>
      </VStack>
      <VStack gap={4} w={'100%'} alignItems='flex-start'>
        <Text mt='20px' color={textColor} fontSize='22px' fontWeight='700' lineHeight='100%'>
          {t('account.team_settings_leader')}
        </Text>
        <Panel padding={4}>
          <Flex flex='1' gap='4' alignItems='center' flexWrap='wrap'>
            <Avatar
              name={teamInfo.user?.name}
              src={base64(teamInfo.user?.profilePhotoPath || '')}
              size="lg"
            />
            <Box>
              <Heading size='sm'>{teamInfo.user?.name}</Heading>
              <Text>{teamInfo.user?.email}</Text>
            </Box>
          </Flex>
        </Panel>
      </VStack>
    </Flex>
  )
}

export default TeamSettings; 